import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Calendar, CheckCircle2, Loader2, MapPin, RefreshCw, X as IconX, Plus, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const DIAS_SEMANA = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function fechaLarga(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return `${DIAS_SEMANA[d.getDay()]} ${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
}

export default function PortalMontaje() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modo, setModo] = useState("elegir"); // "elegir" | "contrapropuesta"
  const [seleccion, setSeleccion] = useState(null);
  const [nombre, setNombre] = useState("");
  const [notas, setNotas] = useState("");
  const [alternativas, setAlternativas] = useState(["", "", ""]);
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/portal-montaje/${token}`);
      setData(data);
      // Marca como visto
      api.post(`/portal-montaje/${token}/visto`).catch(() => {});
    } catch (err) {
      setError(err.response?.data?.detail || "Enlace no válido");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-amber-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-6 max-w-md text-center">
          <IconX className="w-10 h-10 text-red-400 mx-auto mb-2" />
          <div className="text-red-200 font-bold mb-1">Enlace no disponible</div>
          <div className="text-zinc-400 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  const propuesta = data.propuesta || {};
  const estado = propuesta.estado;
  const fechas = propuesta.fechas || [];
  const cliente = data.cliente || {};

  // Estado: ya aceptada
  if (estado === "aceptada") {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 py-8">
        <div className="max-w-xl mx-auto">
          <Header cliente={cliente} peticion={data.peticion} />
          <div className="bg-emerald-500/10 border-2 border-emerald-500/40 rounded-xl p-6 text-center mt-6">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
            <div className="text-lg font-bold text-emerald-200">¡Fecha confirmada!</div>
            <div className="text-sm text-zinc-300 mt-2">
              Has elegido el <b className="text-emerald-300">{fechaLarga(propuesta.fecha_elegida)}</b>
            </div>
            <div className="text-[11px] text-zinc-500 mt-3">
              Te avisaremos antes del montaje. Si necesitas cambiar la fecha, contacta con nosotros.
            </div>
          </div>
          <FooterContacto />
        </div>
      </div>
    );
  }

  // Estado: rechazada — esperando nueva propuesta de Dani
  if (estado === "rechazada") {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 py-8">
        <div className="max-w-xl mx-auto">
          <Header cliente={cliente} peticion={data.peticion} />
          <div className="bg-amber-500/10 border-2 border-amber-500/40 rounded-xl p-6 text-center mt-6">
            <RefreshCw className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <div className="text-lg font-bold text-amber-200">Gracias por proponer otras fechas</div>
            <div className="text-sm text-zinc-300 mt-2">
              Hemos recibido tu contrapropuesta. Te enviaremos una nueva sugerencia en breve.
            </div>
            {propuesta.fechas_alternativas && (
              <div className="text-[11px] text-zinc-500 mt-3">
                Tus fechas: {propuesta.fechas_alternativas.join(" · ")}
              </div>
            )}
          </div>
          <FooterContacto />
        </div>
      </div>
    );
  }

  // Estado: pendiente (UI principal)
  const elegir = async () => {
    if (!seleccion) return toast.error("Selecciona una fecha");
    setSaving(true);
    try {
      await api.post(`/portal-montaje/${token}/elegir`, {
        fecha_elegida: seleccion,
        nombre_cliente: nombre || null,
        notas: notas || null,
      });
      toast.success("¡Fecha confirmada! Gracias 🎉");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const contrapropuesta = async () => {
    const fechasLimpias = alternativas.filter((f) => f && f.length === 10);
    if (fechasLimpias.length === 0) return toast.error("Indica al menos una fecha");
    setSaving(true);
    try {
      await api.post(`/portal-montaje/${token}/contrapropuesta`, {
        fechas_alternativas: fechasLimpias,
        motivo: motivo || null,
        nombre_cliente: nombre || null,
      });
      toast.success("Te enviaremos una nueva propuesta pronto 👍");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 py-8" data-testid="portal-montaje">
      <div className="max-w-xl mx-auto">
        <Header cliente={cliente} peticion={data.peticion} />

        {/* Lugares de montaje */}
        {data.espacios && data.espacios.length > 0 && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mt-6">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> Lugar(es) de montaje
            </div>
            <div className="space-y-1">
              {data.espacios.slice(0, 4).map((e) => (
                <div key={e.id} className="text-sm">
                  <div className="font-semibold">{e.nombre}</div>
                  <div className="text-[11px] text-zinc-400">{e.direccion}{e.ciudad ? `, ${e.ciudad}` : ""}</div>
                </div>
              ))}
              {data.espacios.length > 4 && (
                <div className="text-[11px] text-zinc-500 italic">…y {data.espacios.length - 4} más</div>
              )}
            </div>
          </div>
        )}

        {modo === "elegir" && (
          <div className="mt-6">
            <h2 className="text-xl font-bold text-amber-300 mb-1">📅 Elige tu fecha de montaje</h2>
            <p className="text-sm text-zinc-400 mb-4">
              Hemos terminado de fabricar todo. Selecciona la fecha que mejor te venga:
            </p>

            {propuesta.mensaje_extra && (
              <div className="bg-zinc-900/60 border-l-4 border-amber-500/60 rounded p-3 mb-4 text-xs italic text-zinc-300" data-testid="mensaje-extra">
                "{propuesta.mensaje_extra}"
              </div>
            )}

            <div className="space-y-2" data-testid="opciones-fechas">
              {fechas.map((f, idx) => {
                const sel = seleccion === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setSeleccion(f)}
                    data-testid={`fecha-opcion-${idx}`}
                    className={`w-full text-left p-3.5 rounded-xl border-2 transition-colors flex items-center gap-3 ${
                      sel
                        ? "bg-emerald-500/15 border-emerald-500/60"
                        : "bg-zinc-900/60 border-zinc-800 hover:border-zinc-700"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${
                      sel ? "bg-emerald-500 text-zinc-900" : "bg-zinc-800 text-zinc-400"
                    }`}>
                      {idx + 1}ª
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-sm">{fechaLarga(f)}</div>
                      <div className="text-[11px] text-zinc-500">Orden de preferencia · {idx + 1}</div>
                    </div>
                    {sel && <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <Label className="text-xs text-zinc-400">Tu nombre (opcional)</Label>
                <Input value={nombre} onChange={(e) => setNombre(e.target.value)}
                  data-testid="nombre-cliente-input"
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Notas para el equipo (opcional)</Label>
                <Textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)}
                  data-testid="notas-input"
                  placeholder="Hora preferida, persona de contacto, restricciones…"
                  className="bg-zinc-900 border-zinc-800 text-sm" />
              </div>
            </div>

            <Button onClick={elegir} disabled={saving || !seleccion}
              data-testid="confirmar-fecha-btn"
              className="w-full mt-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold text-base py-6">
              {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
              Confirmar fecha
            </Button>

            <button onClick={() => setModo("contrapropuesta")}
              data-testid="proponer-otras-btn"
              className="w-full mt-3 text-sm text-amber-400 hover:text-amber-300 underline">
              ¿Ninguna te va bien? Proponer otras fechas
            </button>
          </div>
        )}

        {modo === "contrapropuesta" && (
          <div className="mt-6" data-testid="contrapropuesta-form">
            <h2 className="text-xl font-bold text-amber-300 mb-1">🔄 Proponer otras fechas</h2>
            <p className="text-sm text-zinc-400 mb-4">
              Indícanos hasta 3 fechas alternativas y te enviaremos una nueva propuesta:
            </p>

            {alternativas.map((f, idx) => (
              <div key={idx} className="mb-3">
                <Label className="text-xs text-zinc-400">Fecha {idx + 1}{idx > 0 ? " (opcional)" : ""}</Label>
                <Input type="date" value={f}
                  onChange={(e) => {
                    const a = [...alternativas]; a[idx] = e.target.value; setAlternativas(a);
                  }}
                  data-testid={`alt-fecha-${idx}`}
                  className="bg-zinc-900 border-zinc-800" />
              </div>
            ))}

            <Label className="text-xs text-zinc-400">Motivo / notas (opcional)</Label>
            <Textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Por qué no os van las fechas propuestas…"
              data-testid="motivo-input"
              className="bg-zinc-900 border-zinc-800 text-sm" />

            <div className="mt-2">
              <Label className="text-xs text-zinc-400">Tu nombre (opcional)</Label>
              <Input value={nombre} onChange={(e) => setNombre(e.target.value)}
                className="bg-zinc-900 border-zinc-800" />
            </div>

            <Button onClick={contrapropuesta} disabled={saving}
              data-testid="enviar-contrapropuesta-btn"
              className="w-full mt-4 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold py-6">
              {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Sparkles className="w-5 h-5 mr-2" />}
              Enviar contrapropuesta
            </Button>

            <button onClick={() => setModo("elegir")}
              className="w-full mt-3 text-sm text-zinc-400 hover:text-zinc-300 underline">
              ← Volver a elegir entre las propuestas
            </button>
          </div>
        )}

        <FooterContacto />
      </div>
    </div>
  );
}

function Header({ cliente, peticion }) {
  return (
    <div className="text-center" data-testid="portal-header">
      <div className="text-[10px] uppercase tracking-widest text-amber-400/80">RotulaciónDNF · Producción Gráfica</div>
      <h1 className="text-2xl sm:text-3xl font-bold mt-2">{cliente?.nombre || "Cliente"}</h1>
      <div className="text-sm text-zinc-400 mt-1">
        Petición <span className="font-mono text-zinc-300">{peticion?.codigo}</span>
        {peticion?.nombre && <> · {peticion.nombre}</>}
      </div>
    </div>
  );
}

function FooterContacto() {
  return (
    <div className="mt-8 pt-6 border-t border-zinc-800 text-center text-[11px] text-zinc-500">
      <div>¿Dudas? Llámanos al ☎ <a href="tel:+34624491271" className="text-amber-400">+34 624 49 12 71</a></div>
      <div className="mt-1">rotulaciondnf.com</div>
    </div>
  );
}
